/* eslint-disable no-undef */
// Pin landmark: fetch per viewport, cluster kalau zoom-nya jauh.
import LandmarkSvc from '@/services/LandmarkSvc';
import { clusterByGrid, getClusterCellSize } from '@/util/trail/mapCluster';
import { formatNum } from '@/util/number';
import { escapeHtml } from '@/util/trail/voyageMapExtras';

const CLUSTER_ZOOM_THRESHOLD = 8;
const FETCH_DEBOUNCE_MS = 300;

class LandmarkLayer {
  constructor({ map, store, onChange }) {
    this.map = map;
    this.store = store || null;
    this.onChange = onChange || (() => {});
    this.visible = false;
    this.landmarks = [];
    this.layer = null;
    this.icon = null;
    this.fetchTimeout = null;
    this.lastBoundsKey = null;
    this.lastBounds = null;
  }

  toggle(forceState) {
    this.visible = typeof forceState === 'boolean' ? forceState : !this.visible;
    if (!this.visible) {
      if (this.layer) {
        this.layer.clearLayers();
      }
      this.onChange();
      return;
    }
    this.lastBoundsKey = null;
    this.lastBounds = null;
    const storedLandmarks = this.store?.getters['landmark/landmarks'];
    const storedBounds = this.store?.getters['landmark/bounds'];
    if (Array.isArray(storedLandmarks) && storedLandmarks.length) {
      this.landmarks = storedLandmarks;
      this.render();
    }
    if (storedBounds) {
      this.lastBounds = L.latLngBounds(
        [storedBounds.minLatitude, storedBounds.minLongitude],
        [storedBounds.maxLatitude, storedBounds.maxLongitude],
      );
    }
    this.fetchInView();
    this.onChange();
  }

  handleViewportChange() {
    if (!this.visible) return;
    this.scheduleFetch();
  }

  scheduleFetch() {
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
    }
    this.fetchTimeout = setTimeout(() => {
      this.fetchInView();
    }, FETCH_DEBOUNCE_MS);
  }

  async fetchInView() {
    if (!this.map || !this.visible) return;
    const bounds = this.map.getBounds();
    if (!bounds) return;
    const paddedBounds = bounds.pad(0.25);
    if (this.lastBounds && this.lastBounds.contains(paddedBounds)) {
      this.render();
      return;
    }
    const sw = paddedBounds.getSouthWest();
    const ne = paddedBounds.getNorthEast();
    const boundsKey = `${formatNum(sw.lat, 3)}:${formatNum(sw.lng, 3)}:${formatNum(ne.lat, 3)}:${formatNum(ne.lng, 3)}:${this.map.getZoom()}`;
    if (boundsKey === this.lastBoundsKey) {
      this.render();
      return;
    }
    this.lastBoundsKey = boundsKey;
    this.lastBounds = paddedBounds;

    try {
      const response = await LandmarkSvc.landmarks.area({
        minLatitude: sw.lat,
        maxLatitude: ne.lat,
        minLongitude: sw.lng,
        maxLongitude: ne.lng,
      });
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      this.landmarks = data;
      if (this.store) {
        this.store.dispatch('landmark/setLandmarks', {
          landmarks: data,
          bounds: {
            minLatitude: sw.lat,
            maxLatitude: ne.lat,
            minLongitude: sw.lng,
            maxLongitude: ne.lng,
          },
        });
      }
      this.render();
    } catch (error) {
      console.warn('Failed to fetch landmarks area:', error);
    }
  }

  render() {
    if (!this.map || !this.visible) return;
    if (!this.layer) {
      this.layer = L.layerGroup().addTo(this.map);
    }
    if (!this.icon) {
      this.icon = L.icon({
        iconUrl: '/marker/pinpoint.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });
    }
    this.layer.clearLayers();
    if (!this.landmarks.length) return;

    const zoom = this.map.getZoom();
    const useCluster = zoom < CLUSTER_ZOOM_THRESHOLD;
    if (!useCluster) {
      this.landmarks.forEach((landmark) => this.addMarker(landmark));
      return;
    }

    const cellSize = getClusterCellSize(zoom);
    const clusters = clusterByGrid(
      this.landmarks,
      cellSize,
      (landmark) => [parseFloat(landmark.lndmLatitude), parseFloat(landmark.lndmLongitude)],
    );

    clusters.forEach((cluster) => {
      if (cluster.items.length === 1) {
        this.addMarker(cluster.items[0]);
        return;
      }
      const marker = L.marker([cluster.lat, cluster.lng], {
        icon: LandmarkLayer.clusterIcon(cluster.items.length),
      });
      marker.on('click', () => {
        const nextZoom = Math.min(this.map.getZoom() + 2, this.map.getMaxZoom());
        this.map.setView([cluster.lat, cluster.lng], nextZoom);
      });
      marker.addTo(this.layer);
    });
  }

  addMarker(landmark) {
    const lat = parseFloat(landmark.lndmLatitude);
    const lng = parseFloat(landmark.lndmLongitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    const marker = L.marker([lat, lng], {
      icon: this.icon,
      landmarkId: landmark.lndmId,
    });
    const name = landmark.lndmName ? escapeHtml(landmark.lndmName) : '-';
    const type = landmark.lndmType ? escapeHtml(String(landmark.lndmType).toUpperCase()) : '-';
    marker.bindPopup(`<strong>${name}</strong><br>Type: ${type}`);
    marker.addTo(this.layer);
  }

  static clusterIcon(count) {
    return L.divIcon({
      className: 'landmark-cluster-wrapper',
      html: `<div class="landmark-cluster">${count}</div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  }

  destroy() {
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
      this.fetchTimeout = null;
    }
    this.map = null;
    this.store = null;
    this.landmarks = [];
    this.layer = null;
    this.icon = null;
    this.lastBoundsKey = null;
    this.lastBounds = null;
    this.visible = false;
  }
}

export default LandmarkLayer;
